import React from "react";

import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ShieldCheck, SlidersHorizontal, Users } from "lucide-react";

import NavigationWarningModal from "@components/NavigationWarningModal";
import { RecordingNavWarningModal } from "@components/RecordingDock/RecordingNavWarningModal";
import { ScrollArea, ScrollBar } from "@components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { NAVIGATION_PATHS } from "@constants/navigation";

import { useCapabilities, useMe } from "@hooks/queries/use-me";
import { useNavigationWarning } from "@hooks/useNavigationWarning";

import { cn } from "@utils/ui";

import {
  ADMIN_NAVIGATION_GROUP,
  NAVIGATION_GROUPS,
  NavigationGroup,
  NavigationItem,
  PROVIDER_NAVIGATION_GROUPS,
} from "../constants/links";
import { useSidebar } from "../store";

export const NavigationLinks = () => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { isExpanded } = useSidebar();

  // Admin-only groups appended at runtime (kept out of NAVIGATION_GROUPS so the
  // static nav config/test stays stable).
  const { data: me } = useMe();
  const { can } = useCapabilities();
  // Providers get their own portal nav (Dashboard / Requests / Account) instead
  // of the referrer/expert app nav (MDRS-157).
  const isProvider = me?.user?.user_type === "provider";
  const portalGroups = isProvider ? PROVIDER_NAVIGATION_GROUPS : NAVIGATION_GROUPS;
  const baseGroups: NavigationGroup[] = me?.user?.is_admin
    ? [
        ...portalGroups,
        {
          section: "Admin",
          items: [
            {
              id: "access-control",
              label: "Access Control",
              icon: <ShieldCheck className="size-5" strokeWidth={2} />,
              path: NAVIGATION_PATHS.ACCESS_CONTROL,
            },
            {
              id: "users-admin",
              label: "Users",
              icon: <Users className="size-5" strokeWidth={2} />,
              path: NAVIGATION_PATHS.USERS_ADMIN,
            },
            {
              id: "access-config",
              label: "Access Config",
              icon: <SlidersHorizontal className="size-5" strokeWidth={2} />,
              path: NAVIGATION_PATHS.ACCESS_CONFIG,
            },
            ...ADMIN_NAVIGATION_GROUP.items,
          ],
        },
      ]
    : portalGroups;

  // Referrer portal (MDRS-157) shows a focused nav: their case list is the
  // landing/home, so the "Cases" nav item is hidden (reached without a button),
  // and "New case" is already gone (referrers lost cases:create — admins create
  // cases for them). This is a per-portal presentation choice on top of the
  // capability gating below, so it keys off user_type rather than a capability.
  const isReferrer = me?.user?.user_type === "referrer";
  const REFERRER_HIDDEN_NAV_IDS = new Set(["cases", "new-case"]);

  // Access levels v1: drop items the user lacks the capability for (e.g. a
  // referrer sees neither Invoice Studio nor Resources; a disabled provider sees
  // only ungated items), then drop any group left empty. Items without a
  // `capability` are always shown.
  const groups: NavigationGroup[] = baseGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          (!item.capability || can(item.capability)) &&
          !(isReferrer && REFERRER_HIDDEN_NAV_IDS.has(item.id))
      ),
    }))
    .filter((group) => group.items.length > 0);

  const {
    showWarningModal,
    handleNavigationWithWarning,
    handleConfirmNavigation,
    handleCancelNavigation,
    isRecordingWarning,
    recordingExamineeName,
  } = useNavigationWarning();

  const isActiveRoute = (path: string) => {
    return pathname === path || pathname.startsWith(path);
  };

  const renderNavigationItem = (item: NavigationItem) => {
    const { id, label, icon, path, badge } = item;
    const active = isActiveRoute(path);

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      // Pass the target so the recording guard can tell an in-case return from
      // a case-leaving navigation.
      handleNavigationWithWarning(() => navigate({ to: path }), path);
    };

    const link = (
      // aria-label keeps the item reachable by its label even when the text is
      // visually hidden in the collapsed rail (and sources the hover tooltip).
      <a
        href={path}
        aria-label={label}
        aria-current={active ? "page" : undefined}
        className="block w-full outline-none"
        onClick={handleClick}
      >
        <div
          className={cn(
            "relative mx-2.5 my-0.5 flex items-center gap-2.5 rounded-[9px] px-2.5 py-2",
            "text-left text-[13.5px] leading-[1.1] transition-[background,color,box-shadow] duration-150",
            "cursor-pointer whitespace-nowrap",
            // Focus ring (keyboard) — design token.
            "focus-visible:shadow-nav-focus focus-visible:outline-none",
            active
              ? "bg-primary font-semibold text-primary-foreground shadow-nav-active dark:shadow-nav-active-dark"
              : "font-normal text-foreground-muted hover:bg-primary/[0.07] hover:text-foreground dark:text-foreground-light/70 dark:hover:bg-white/[0.06] dark:hover:text-foreground-light",
            !isExpanded && "justify-center"
          )}
        >
          <span
            className={cn(
              "relative flex size-5 shrink-0 items-center justify-center [&_svg]:size-[18px]",
              active
                ? "text-primary-foreground"
                : "text-foreground-muted/80 dark:text-foreground-light/55"
            )}
          >
            {icon}
            {/* Collapsed badge collapses to a danger dot on the icon corner. */}
            {badge && !isExpanded && (
              <span className="absolute -right-px -top-px size-[9px] rounded-full border-2 border-background-white bg-destructive dark:border-background-dark" />
            )}
          </span>

          {isExpanded && (
            <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              {label}
            </span>
          )}

          {/* Count badge (expanded). Style-only today — no item carries a live
              count yet, but the styling is ready for when one does. */}
          {badge && isExpanded && (
            <span
              className={cn(
                "ml-auto rounded-full px-[7px] py-px text-[11px] font-bold leading-[1.5]",
                active
                  ? "bg-primary-foreground text-primary"
                  : "bg-primary text-primary-foreground"
              )}
            >
              {badge}
            </span>
          )}
        </div>
      </a>
    );

    return (
      <li key={id} className="w-full">
        {isExpanded ? (
          link
        ) : (
          // Collapsed rail: reveal the label as a tooltip on hover/focus. Radix
          // portals the content so it escapes the nav's scroll container.
          <Tooltip>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right" sideOffset={14}>
              {label}
            </TooltipContent>
          </Tooltip>
        )}
      </li>
    );
  };

  return (
    <>
      <ScrollArea
        className={cn("flex-1 overflow-auto", isExpanded ? "py-2" : "py-1.5")}
      >
        <div className="flex flex-col">
          {groups.map((group, index) => (
            <div key={group.section ?? `group-${index}`} className="flex flex-col">
              {group.section &&
                (isExpanded ? (
                  // Group label: a 1px top hairline marks the section start, so
                  // the label reads as a divider above its items rather than
                  // blending into them.
                  <span className="mx-2.5 mt-2 block border-t border-border px-2 pb-1.5 pt-3.5 font-montserrat text-[10.5px] font-bold uppercase leading-none tracking-[0.12em] text-foreground-muted/70 dark:border-white/[0.08] dark:text-foreground-light/40">
                    {group.section}
                  </span>
                ) : (
                  // Collapsed: the label becomes a centered divider.
                  <div className="mx-[22px] my-3 h-px bg-border dark:bg-white/10" />
                ))}
              <ul className="m-0 flex list-none flex-col p-0">
                {group.items.map(renderNavigationItem)}
              </ul>
            </div>
          ))}
        </div>
        <ScrollBar />
      </ScrollArea>

      {/* Report-preview unsaved-changes guard. */}
      <NavigationWarningModal
        isOpen={showWarningModal && !isRecordingWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
      />

      {/* Recording case-boundary guard — a live recording is about
          to leave its case via a sidebar link. */}
      <RecordingNavWarningModal
        isOpen={showWarningModal && isRecordingWarning}
        examineeName={recordingExamineeName}
        onStay={handleCancelNavigation}
        onFinishAndLeave={handleConfirmNavigation}
      />
    </>
  );
};
